import { BoxProps, DiscountBadge } from "@vinomofo/components";
import { forwardRef } from "react";

import { OfferSoldOut } from "./OfferSoldOut";
import type { RefObject } from "react";
import { RelativeSizeImage } from "../relative-size-image";
import { Roundel } from "../roundel";
import { ScrollingImageContainer } from "./ScrollingImageContainer";
import { StorefrontImage } from "../../types";
import styled from "styled-components";
import { DiscountDisplay } from "../../types/vino-data-types";
import { discountDisplays } from "../../utils/constants";

export interface OfferHeroImageWideProps extends BoxProps {
  name: string;
  image: StorefrontImage;
  roundelsImage?: StorefrontImage;
  isAvailable: boolean;
  discountPercent: number;
  discountDisplay: DiscountDisplay;
  discountDollarValue: number;
  showDiscountBadge: boolean;
  bottleShape: string;
}

const StyledRelativeSizeImage = styled(RelativeSizeImage)`
  img {
    @media ${({ theme }) => theme.mq.max.tablet} {
      object-position: 90% bottom !important;
    }
  }
`;

const StyledOfferSoldOut = styled(OfferSoldOut)`
  transform: translateX(-50%);
  left: 50%;
  top: 180px;
  @media ${({ theme }) => theme.mq.max.tablet} {
    transform: none;
    width: 63%;
    top: 80px;
  }
`;

const StyledDiscountBadge = styled(DiscountBadge)`
  transform: translateX(-120%);
  left: 50%;
  top: 180px;
  width: 80px;
  height: 80px;
  > span:first-of-type {
    font-size: 30px;
    font-weight: 900;
  }
  > p:first-of-type {
    font-size: 14px;
    font-weight: 700;
  }
  @media ${({ theme }) => theme.mq.max.tablet} {
    transform: none;
    left: auto;
    right: 17%;
    width: 80px;
    height: 80px;
    > span:first-of-type {
      font-size: 30px;
    }
    > p:first-of-type {
      font-size: 14px;
      font-weight: 500;
    }
  }
`;

export const OfferHeroImageSingleWide = forwardRef(
  (
    {
      name,
      image,
      isAvailable,
      discountPercent,
      discountDollarValue,
      discountDisplay,
      showDiscountBadge,
      roundelsImage,
      bottleShape,
      ...rest
    }: OfferHeroImageWideProps,
    ref: RefObject<HTMLElement>
  ): JSX.Element => {
    return (
      <ScrollingImageContainer
        scrollOnElementId="hero-scroll-stop"
        backgroundColor="#FFF"
        top={[0, 35, 100]}
        position="fixed"
        {...rest}
        ref={ref}
      >
        <StyledRelativeSizeImage
          name={name}
          ratio={1440 / 775}
          image={image}
          overflow="visible"
          height={
            bottleShape?.toLowerCase() === "small whisky" ? "169%" : "135%"
          }
        />
        {!isAvailable && <StyledOfferSoldOut position="absolute" />}
        {isAvailable && showDiscountBadge && (
          <StyledDiscountBadge
            position="absolute"
            discountDisplay={discountDisplay}
            amount={
              discountDisplay === discountDisplays.dollar
                ? discountDollarValue
                : discountPercent
            }
            variant="lg"
          />
        )}
        {roundelsImage && (
          <Roundel
            width={["100px", "110px", "150px"]}
            height={["100px", "110px", "150px"]}
            isExpired={!isAvailable}
            image={roundelsImage}
            bottom="10px"
            left={["auto", "auto", "calc(54%)"]}
            right={["2%", "2%", "auto"]}
            zIndex="1"
          />
        )}
      </ScrollingImageContainer>
    );
  }
);

OfferHeroImageSingleWide.displayName = "OfferHeroImageSingleWide";
